/**
 * Vendors API Client
 * Frontend API calls for managing vendor catalog records
 */

import { apiCall } from '../api';
import type {
  Vendor,
  VendorsResponse,
  VendorResponse,
  DeleteVendorResponse,
  VendorFilters,
  CreateVendorRequest,
  UpdateVendorRequest,
} from '@/types/api';

/**
 * Fetch vendors with optional search and active status filters.
 */
export async function fetchVendors(filters: VendorFilters = {}): Promise<Vendor[]> {
  const params = new URLSearchParams();

  if (filters.search) params.set('search', filters.search);
  if (filters.isActive !== undefined) params.set('isActive', String(filters.isActive));

  const queryString = params.toString();
  const url = queryString ? `/api/vendors?${queryString}` : '/api/vendors';

  const response = await apiCall<VendorsResponse>(url, {
    method: 'GET',
  });

  return response.vendors;
}

/**
 * Fetch a single vendor by ID.
 */
export async function fetchVendor(id: number): Promise<Vendor> {
  const response = await apiCall<VendorResponse>(`/api/vendors/${id}`, {
    method: 'GET',
  });

  return response.vendor;
}

/**
 * Create a vendor.
 */
export async function createVendor(data: CreateVendorRequest): Promise<Vendor> {
  const response = await apiCall<VendorResponse>('/api/vendors', {
    method: 'POST',
    body: JSON.stringify(data),
  });

  return response.vendor;
}

/**
 * Update an existing vendor.
 */
export async function updateVendor(id: number, data: UpdateVendorRequest): Promise<Vendor> {
  const response = await apiCall<VendorResponse>(`/api/vendors/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  return response.vendor;
}

/**
 * Deactivate a vendor.
 */
export async function deleteVendor(id: number): Promise<DeleteVendorResponse> {
  return apiCall<DeleteVendorResponse>(`/api/vendors/${id}`, {
    method: 'DELETE',
  });
}
